import { useCallback, useRef } from 'react';

import { AxiosResponse } from 'axios';

import {
  API_RESPONSE_STATUS,
  COSMOS_POLL_STATUS,
  JOURNEY_TYPES,
} from '../../constants';
import { initiateCosmosPolling } from '../../helpers';
import { initProgress, initProgressPoll } from 'src/utils/services';

export const useInitProgress = ({
  journeyType,
}: {
    journeyType: (typeof JOURNEY_TYPES)[keyof typeof JOURNEY_TYPES];
}) => {
  const pollClientRef = useRef<any>(null);

  return useCallback(
    async (
      onSuccess: (progressData: any) => void,
      onFailure: (error?: any) => void
    ) => {
      if (pollClientRef.current) {
        pollClientRef.current.abort();
        pollClientRef.current = null;
      }

      try {
        const res = await initProgress(journeyType);
        const { data } = res;
        const { statusCode, status, result, error } = data || {};
        const normalizedStatus = status?.toLowerCase();
        const shouldFail =
          statusCode !== 200 ||
          normalizedStatus !== COSMOS_POLL_STATUS.SUCCESS.toLocaleLowerCase() ||
          !result ||
          (!result.poll && result.progress?.length === 0) ||
          !result.pollConfig;

        if (shouldFail) {
          onFailure({ message: error?.message });
          return;
        }

        if (
          !result.poll &&
          (result.progress?.dataCollectionOrder?.length > 0 ||
            result.progress?.progress?.length > 0)
        ) {
          onSuccess(result.progress);
          return;
        }

        const { maxAttempts, pollInterval } = result.pollConfig;

        pollClientRef.current = initiateCosmosPolling<AxiosResponse<any>>({
          pollingApiFn: () => initProgressPoll(journeyType),
          maxAttempts: maxAttempts,
          pollInterval: pollInterval,
          continuePollingCondition: pollRes => {
            return (
              pollRes?.status === 200 &&
              pollRes?.data?.status?.toLowerCase() ===
                API_RESPONSE_STATUS.SUCCESS.toLocaleLowerCase() &&
              pollRes?.data?.result?.pollStatus === COSMOS_POLL_STATUS.CONTINUE
            );
          },
          onPollSuccess: pollRes => {
            if (
              pollRes?.data?.status?.toLowerCase() ===
                API_RESPONSE_STATUS.SUCCESS.toLocaleLowerCase() &&
              pollRes?.data?.result?.pollStatus ===
                COSMOS_POLL_STATUS.SUCCESS &&
              pollRes?.data?.result?.progress
            ) {
              onSuccess(pollRes.data.result.progress);
            } else {
              onFailure({ message: pollRes?.data?.error?.message });
            }
          },
          onPollFailed: error => {
            onFailure(error);
          },
          onPollExpire: () => {
            onFailure();
          },
        });
      } catch (error: any) {
        onFailure(error);
      }
    },
    [journeyType]
  );
};
